import {
  Box,
  Button,
  FormControlLabel,
  Grid2 as Grid,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { FormEvent, useCallback, useContext, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import LoadState from "./loading";
import LoadingIndicator from "./loading-indicator";
import { Alert } from "@mui/material";
import csrf from "requests/csrf";
import { Helmet } from "react-helmet";
import SessionContext from "contexts/session";

enum SubmitState {
  Ready,
  Validate,
  Error,
  Success,
}

const eserviceLink = (
  <a href="http://eservice.cerner.com/">
    <FormattedMessage
      id="ContactUs.eserviceURL"
      defaultMessage="eservice.cerner.com"
    />
  </a>
);

const ContactForm = (): JSX.Element => {
  const session = useContext(SessionContext);
  const [submitStatus, setSubmitStatus] = useState(SubmitState.Ready);
  const [loading, setLoading] = useState(LoadState.Done);
  const [first, setFirst] = useState(session.user.first_name || "");
  const [last, setLast] = useState(session.user.last_name || "");
  const [email, setEmail] = useState(session.user.email || "");
  const [internalInd, setInternalInd] = useState(false);
  const [body, setBody] = useState("");
  const i18n = useIntl();

  const handleAlertClose = useCallback(() => {
    setSubmitStatus(SubmitState.Ready);
  }, []);

  const clearFields = () => {
    setFirst("");
    setLast("");
    setEmail("");
    setInternalInd(false);
    setBody("");
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(LoadState.Loading);
    setSubmitStatus(SubmitState.Validate);

    if (!first || !last || !email || !body) {
      return setLoading(LoadState.Error);
    }

    const staticFields = {
      first,
      last,
      email,
      internalInd,
      body,
    };

    const resp = await fetch(`/api/contact`, {
      method: "POST",
      headers: { "content-type": "application/json", "X-CSRF-TOKEN": csrf() },
      body: JSON.stringify(staticFields),
    });

    if (!resp.ok) {
      setLoading(LoadState.Error);
      setSubmitStatus(SubmitState.Error);
      clearFields();
      return;
    }

    setLoading(LoadState.Done);
    setSubmitStatus(SubmitState.Success);
    clearFields();
  };

  return (
    <Box m={5}>
      <form onSubmit={handleSubmit}>
        {/* @ts-ignore */}
        <Helmet>
          <title>
            {i18n.formatMessage({
              id: "ContactForm.contactUs",
              defaultMessage: "Contact Us",
            })}
          </title>
        </Helmet>
        <Grid container spacing={2} flexDirection="column">
          <Grid container spacing={2}>
            <Typography variant="h1">
              <FormattedMessage
                id="ContactForm.contactUs"
                defaultMessage="Contact Us"
              />
            </Typography>
          </Grid>
          <Grid container spacing={2} flexDirection="column">
            <Grid container spacing={2}>
              <Typography>
                <FormattedMessage
                  id="ContactForm.pleaseUseSR"
                  defaultMessage="Please use this form to report issues with the functionality of the Model Experience website, such as error messages or pages not loading. For questions or issues related to product recommendations or configuration standards, please log a ticket to {eserviceLink}."
                  values={{
                    eserviceLink,
                  }}
                />
              </Typography>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid>
              <TextField
                required
                error={submitStatus === SubmitState.Validate && !first}
                helperText={
                  submitStatus === SubmitState.Validate &&
                  !first && (
                    <FormattedMessage
                      id="ContactForm.thisFieldIsRequired"
                      defaultMessage="This field is required."
                    />
                  )
                }
                value={first}
                label={
                  <FormattedMessage
                    id="ContactForm.firstName"
                    defaultMessage="First Name"
                  />
                }
                name="first"
                id="first"
                onChange={(e) => setFirst(e.target.value)}
                slotProps={{ htmlInput: { maxLength: 255 } }}
              />
            </Grid>
            <Grid>
              <TextField
                required
                error={submitStatus === SubmitState.Validate && !last}
                helperText={
                  submitStatus === SubmitState.Validate &&
                  !last && (
                    <FormattedMessage
                      id="ContactForm.thisFieldIsRequired"
                      defaultMessage="This field is required."
                    />
                  )
                }
                value={last}
                label={
                  <FormattedMessage
                    id="ContactForm.lastName"
                    defaultMessage="Last Name"
                  />
                }
                name="last"
                id="last"
                onChange={(e) => setLast(e.target.value)}
                slotProps={{ htmlInput: { maxLength: 255 } }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid>
              <TextField
                required
                type="email"
                error={submitStatus === SubmitState.Validate && !email}
                helperText={
                  submitStatus === SubmitState.Validate &&
                  !email && (
                    <FormattedMessage
                      id="ContactForm.thisFieldIsRequired"
                      defaultMessage="This field is required."
                    />
                  )
                }
                value={email}
                label={
                  <FormattedMessage
                    id="ContactForm.email"
                    defaultMessage="Email"
                  />
                }
                name="email"
                id="email"
                onChange={(e) => setEmail(e.target.value)}
                slotProps={{ htmlInput: { maxLength: 255 } }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid>
              <FormControlLabel
                control={
                  <Switch
                    value={internalInd}
                    name="internalInd"
                    id="internalInd"
                    onChange={(e) => setInternalInd(e.target.checked)}
                  />
                }
                label={
                  <FormattedMessage
                    id="ContactForm.oracleHealthEmployee"
                    defaultMessage="Oracle Health Employee"
                  />
                }
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid>
              <TextField
                required
                error={submitStatus === SubmitState.Validate && !body}
                helperText={
                  submitStatus === SubmitState.Validate &&
                  !body && (
                    <FormattedMessage
                      id="ContactForm.thisFieldIsRequired"
                      defaultMessage="This field is required."
                    />
                  )
                }
                value={body}
                label={
                  <FormattedMessage
                    id="ContactForm.howMayWeHelpYou"
                    defaultMessage="How may we help you?"
                  />
                }
                name="body"
                id="body"
                onChange={(e) => setBody(e.target.value)}
                multiline
                minRows={6}
                maxRows={20}
                sx={{ minWidth: "400px" }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Button
              type="submit"
              color="primary"
              disabled={loading === LoadState.Loading}
            >
              {loading === LoadState.Loading ? (
                <LoadingIndicator loading={loading} />
              ) : (
                <FormattedMessage
                  id="ContactForm.submit"
                  defaultMessage="Submit"
                />
              )}
            </Button>
          </Grid>
        </Grid>
        <Snackbar
          autoHideDuration={6000}
          open={submitStatus === SubmitState.Error}
          onClose={handleAlertClose}
        >
          <Alert severity="error">
            <FormattedMessage
              id="ContactForm.anErrorHasOcurredPleaseTryAgainLater"
              defaultMessage="An error has ocurred. Please try again later"
            />
          </Alert>
        </Snackbar>
        <Snackbar
          autoHideDuration={6000}
          open={submitStatus === SubmitState.Success}
          onClose={handleAlertClose}
        >
          <Alert severity="success">
            <FormattedMessage
              id="ContactForm.contactRequestSubmittedSuccessfully"
              defaultMessage="Contact request submitted successfully!"
            />
          </Alert>
        </Snackbar>
      </form>
    </Box>
  );
};

export default ContactForm;
